import {
  MCDANIEL_FOLDER_ID,
  NOVI_FOLDER_ID,
  SYSTEM_DEFAULT_FORECAST_FOLDER_ID,
  WORKING_FORECAST_FOLDER_ID
} from "constants/settings.constants";

import useBetaFeatures from "hooks/useBetaFeatures";

import { useNoviAccess } from "components/arps/hooks/useHasNoviAccess";

/**
 * Decides what a persisted `defaultForecastSource` value actually means for this user, in one
 * place, so every writer of forecast selection agrees.
 *
 * A saved value can outlive the thing it names: an org loses its Novi data source, or a user
 * carries a Working Forecasts default without the beta feature that renders the node. Applying
 * such a value literally checks a source the user does not have and puts it in every request
 * payload. Both writers — the loader's applier and clear-forecasts — must resolve it the same
 * way, or the panel and the payload disagree depending on which ran last.
 *
 * `isReady` is separate from the value because a Novi-dependent value cannot be interpreted at
 * all until the availability query settles; applying it early would check Novi and then uncheck
 * it, which is the flicker US7 forbids.
 */
export function useEffectiveForecastFolderId(storedFolderId: Maybe<string>) {
  const { isAuthoritative, hasNoviAccess } = useNoviAccess();
  const { hasFeature } = useBetaFeatures();

  const dependsOnNoviAccess =
    storedFolderId === SYSTEM_DEFAULT_FORECAST_FOLDER_ID ||
    storedFolderId === NOVI_FOLDER_ID;

  // Downgrade only on an AUTHORITATIVE negative. The data-source request resolves { ok: false }
  // instead of rejecting, so a soft failure is not evidence the org lacks Novi — treating it as
  // such would strip a saved Novi default for the whole session on one transient blip.
  const noviUnavailable = dependsOnNoviAccess && isAuthoritative && !hasNoviAccess;

  // Working Forecasts is gated on a beta feature rather than on a data source, so it needs its
  // own check on its own axis — hasNoviAccess says nothing about it.
  const workingForecastsUnavailable =
    storedFolderId === WORKING_FORECAST_FOLDER_ID && !hasFeature("Forecasting");

  // isAuthoritative, not isResolved. These must be the SAME axis: narrowing above requires an
  // authoritative answer, so gating readiness on the weaker `isResolved` opens a window —
  // resolved but not authoritative, which is what a soft failure looks like — where a
  // Novi-dependent value is applied LITERALLY because it cannot yet be narrowed. For a stored
  // "-4" that means useNovi/excludeNovi: false in every chart request for an org that may have
  // no Novi credentials, with no Novi node in the panel to reveal it. It cannot self-heal
  // either: the request never rejects so React Query never retries, and if a later refetch is
  // authoritative the applier's pristine guard stands the correction down, because the saved
  // value never moved.
  //
  // Waiting costs the "-4" cohort nothing: a pristine forecast store already starts
  // McDaniel-only with excludeNoviForecasts: true, which is the correct end state for a
  // non-Novi org anyway. It errs toward one source too few rather than one the org may not be
  // entitled to — the same trade useClearForecasts documents. (EVA-180)
  return {
    isReady: !dependsOnNoviAccess || isAuthoritative,
    effectiveFolderId:
      noviUnavailable || workingForecastsUnavailable ? MCDANIEL_FOLDER_ID : storedFolderId
  };
}

export default useEffectiveForecastFolderId;
